import { useCallback, useMemo } from 'react';
import { useSyncExternalStore } from 'react';
import type { DayOfWeek, DayPlan } from '../types/exercise';
import { getDayOfWeek } from './scheduler';
import { getPlanForDay } from '../data/plan';
import { dateKey, getDoneSets, subscribe } from './setProgress';

/** Liefert den Montag (00:00 Lokalzeit) der Woche, in der `date` liegt. */
export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const dow = d.getDay();
  // JS getDay: 0=So … 6=Sa. ISO-Woche startet Montag.
  const offsetToMon = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offsetToMon);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getWeekDates(date: Date): Date[] {
  const start = getWeekStart(date);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export interface DayStats {
  date: Date;
  dayKey: string;
  dayOfWeek: DayOfWeek;
  plan: DayPlan;
  completedExercises: number;
  totalExercises: number;
  totalSetsDone: number;
  totalSets: number;
}

type GetDone = (dayKey: string, exerciseId: string) => readonly number[];

export function computeDayStats(date: Date, getDone: GetDone): DayStats {
  const dow = getDayOfWeek(date);
  const plan = getPlanForDay(dow);
  const dKey = dateKey(date);
  let completedExercises = 0;
  let totalExercises = 0;
  let totalSetsDone = 0;
  let totalSets = 0;
  if (plan.kind === 'training') {
    totalExercises = plan.entries.length;
    for (const entry of plan.entries) {
      const done = getDone(dKey, entry.exerciseId);
      const cappedDone = Math.min(done.length, entry.sets);
      totalSetsDone += cappedDone;
      totalSets += entry.sets;
      if (cappedDone >= entry.sets) completedExercises += 1;
    }
  }
  return {
    date,
    dayKey: dKey,
    dayOfWeek: dow,
    plan,
    completedExercises,
    totalExercises,
    totalSetsDone,
    totalSets,
  };
}

export interface WeekTotals {
  completedExercises: number;
  totalExercises: number;
  totalSetsDone: number;
  totalSets: number;
}

export function sumWeekTotals(stats: readonly DayStats[]): WeekTotals {
  return stats.reduce<WeekTotals>(
    (acc, s) => ({
      completedExercises: acc.completedExercises + s.completedExercises,
      totalExercises: acc.totalExercises + s.totalExercises,
      totalSetsDone: acc.totalSetsDone + s.totalSetsDone,
      totalSets: acc.totalSets + s.totalSets,
    }),
    { completedExercises: 0, totalExercises: 0, totalSetsDone: 0, totalSets: 0 },
  );
}

/** Fingerprint, dessen String-Identitaet sich aendert, wann immer sich die Wochen-Statistik bewegt. */
function weekFingerprint(today: Date): string {
  const dates = getWeekDates(today);
  return dates
    .map((d) => {
      const k = dateKey(d);
      const plan = getPlanForDay(getDayOfWeek(d));
      if (plan.kind !== 'training') return `${k}:${plan.kind}`;
      const entryFp = plan.entries
        .map((e) => `${e.exerciseId}/${getDoneSets(k, e.exerciseId).length}`)
        .join(',');
      return `${k}:${entryFp}`;
    })
    .join('|');
}

export function useWeekStats(today: Date): DayStats[] {
  const sub = useCallback((cb: () => void) => subscribe(cb), []);
  const getSnap = useCallback(() => weekFingerprint(today), [today]);
  const fp = useSyncExternalStore(sub, getSnap, getSnap);
  return useMemo(
    () => getWeekDates(today).map((d) => computeDayStats(d, getDoneSets)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [today, fp],
  );
}
